/**
 * reminders.jsx  –  Alarm & Hatırlatıcı + Manuel Termin
 * Sade, hızlı giriş: saat büyük/dokunulabilir, gün pilleri, tek tıkla kaydet
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bell, X, Plus, Trash2, AlarmClock, CalendarDays, BellOff, Check } from "lucide-react";
import { toast } from "sonner";
import api from "./api";
import { playBeep } from "./utils/soundUtils";

/* ─── sabitler ─── */
const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
// JS getDay(): 0=Pazar … günü 0=Pzt olarak normalize ediyoruz
const todayDowIdx = () => { const d = new Date().getDay(); return d === 0 ? 6 : d - 1; };

const REPEAT_OPTS = [
  { v: "once",    l: "Bir kez"   },
  { v: "daily",   l: "Her gün"   },
  { v: "weekdays",l: "Hafta içi" },
  { v: "weekly",  l: "Haftalık"  },
  { v: "monthly", l: "Aylık"     },
  { v: "yearly",  l: "Yıllık"    },
];

const QUICK_TIMES = ["07:00","08:00","09:00","10:00","12:00","14:00","17:00","18:00"];

const todayStr  = () => new Date().toISOString().slice(0, 10);
const nowHHMM   = () => { const d = new Date(); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; };

/* ─── browser bildirim & ses ─── */
async function requestNotifPerm() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  return (await Notification.requestPermission()) === "granted";
}
function fireNotif(title, body) {
  if (Notification.permission === "granted")
    new Notification(`⏰ ${title}`, { body, icon: "/favicon.ico" });
}

/* ─── bugün çalmalı mı? ─── */
function shouldFireToday(rem) {
  if (!rem.active) return false;
  const today   = todayStr();
  const todayDow = todayDowIdx(); // 0=Pzt … 6=Paz
  const rep = rem.repeat;

  if (rep === "once")    return rem.date === today;
  if (rep === "daily")   return true;
  if (rep === "weekdays") return todayDow <= 4; // Pzt-Cum
  if (rep === "weekly") {
    // weekdays array varsa → seçilen günlerde çalsın
    if (rem.weekdays?.length) return rem.weekdays.includes(todayDow);
    // yoksa base tarihten itibaren her 7 günde
    if (!rem.date) return false;
    const diff = (new Date(today) - new Date(rem.date));
    return diff >= 0 && diff % (7 * 86400000) === 0;
  }
  if (rep === "monthly") {
    if (!rem.date) return false;
    const base = new Date(rem.date);
    const now  = new Date(today);
    return base.getDate() === now.getDate() && base <= now;
  }
  if (rep === "yearly") {
    if (!rem.date) return false;
    const base = new Date(rem.date);
    const now  = new Date(today);
    return base.getMonth() === now.getMonth() && base.getDate() === now.getDate() && base <= now;
  }
  return false;
}

/* ─── RepeatLabel yardımcı ─── */
function repeatLabel(rem) {
  const opt = REPEAT_OPTS.find(o => o.v === rem.repeat);
  const base = opt?.l || rem.repeat;
  if (rem.repeat === "weekly" && rem.weekdays?.length) {
    return DAYS.filter((_, i) => rem.weekdays.includes(i)).join(" · ");
  }
  return base;
}

/* ════════════════════════════════════════════
   AlarmChecker — arka planda her 30sn kontrol
═════════════════════════════════════════════ */
export function AlarmChecker() {
  const fired = useRef(new Set());
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/reminders");
        const now = nowHHMM(), today = todayStr();
        // Tüm tetiklenecek alarmları topla, TEK bildirim olarak göster
        const newAlarms = data.filter(r => {
          if (r.type !== "alarm") return false;
          if (!shouldFireToday(r)) return false;
          const key = `${r.id}-${today}-${r.time}`;
          if (fired.current.has(key)) return false;
          if (r.time !== now) return false;
          fired.current.add(key);
          return true;
        });
        if (newAlarms.length === 0) return;
        playBeep("notify");
        if (newAlarms.length === 1) {
          fireNotif(newAlarms[0].title, newAlarms[0].note || "ZEN ERP");
          toast.warning(`⏰ ${newAlarms[0].title}`, { id: "alarm-notif", description: newAlarms[0].note || undefined, duration: 8000 });
        } else {
          fireNotif("ZEN ERP Alarmlar", `${newAlarms.length} alarm aktif`);
          toast.warning(`⏰ ${newAlarms.length} Alarm`, { id: "alarm-notif", description: newAlarms.map(r => r.title).join(" · "), duration: 8000 });
        }
      } catch {}
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);
  return null;
}

/* ════════════════════════════════════════════
   AlarmModal — saat'e tıklayınca açılır
   Tasarım: büyük saat girişi, gün pilleri, hızlı ekle
═════════════════════════════════════════════ */
const emptyForm = () => ({
  title: "", time: "09:00", date: todayStr(),
  repeat: "once", weekdays: [], type: "alarm", note: "", active: true,
});

export function AlarmModal({ onClose }) {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding,  setAdding]  = useState(false);
  const [form,    setForm]    = useState(emptyForm());
  const titleRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/reminders");
    setList(data.filter(r => r.type === "alarm"));
    setLoading(false);
  }, []);

  useEffect(() => { load(); requestNotifPerm(); }, [load]);
  useEffect(() => { if (adding) setTimeout(() => titleRef.current?.focus(), 50); }, [adding]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleDay = (i) => setForm(p => {
    const w = p.weekdays.includes(i) ? p.weekdays.filter(d => d !== i) : [...p.weekdays, i];
    return { ...p, weekdays: w };
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Başlık girin"); titleRef.current?.focus(); return; }
    await api.post("/reminders", form);
    setForm(emptyForm()); setAdding(false);
    toast.success("Alarm kaydedildi");
    load();
  };

  const toggle = async (r) => { await api.put(`/reminders/${r.id}`, { active: !r.active }); load(); };
  const del    = async (r) => { if (!window.confirm("Bu hatırlatma silinsin mi?")) return; await api.delete(`/reminders/${r.id}`); toast.success("Silindi"); load(); };

  const needDate     = ["once","monthly","yearly"].includes(form.repeat);
  const needWeekdays = form.repeat === "weekly";

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="zen-panel w-full sm:max-w-sm zen-anim-up max-h-[94vh] overflow-y-auto rounded-b-none sm:rounded-2xl">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-200 dark:border-[#22304A]">
          <div className="flex items-center gap-2">
            <AlarmClock className="w-4 h-4 text-[#E5322A]" />
            <span className="font-bold text-sm">Alarmlar</span>
            {list.length > 0 && <span className="zen-chip !text-[10px]">{list.length}</span>}
          </div>
          <div className="flex items-center gap-1.5">
            {!adding && (
              <button onClick={() => setAdding(true)} data-testid="alarm-add-btn"
                className="zen-btn-primary !text-xs !py-1.5 !px-3">
                <Plus className="w-3.5 h-3.5" /> Yeni
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Hızlı ekleme formu ── */}
        {adding && (
          <div className="p-4 border-b border-slate-200 dark:border-[#22304A] space-y-3 bg-[#E5322A]/4">

            {/* BÜYÜK SAAT */}
            <div className="flex flex-col items-center gap-1">
              <input type="time" value={form.time} onChange={e => set("time", e.target.value)}
                data-testid="alarm-time-input"
                className="text-4xl font-black tracking-tight bg-transparent border-0 outline-none text-slate-900 dark:text-white text-center w-40 cursor-pointer"
                style={{ fontVariantNumeric: "tabular-nums" }}
              />
              {/* Hızlı saat seçici */}
              <div className="flex flex-wrap gap-1.5 justify-center">
                {QUICK_TIMES.map(t => (
                  <button key={t} onClick={() => set("time", t)}
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-all
                      ${form.time === t ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-[#E5322A]/50"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Başlık */}
            <input ref={titleRef} className="zen-input !text-sm w-full" placeholder="Hatırlatıcı başlığı..."
              value={form.title} onChange={e => set("title", e.target.value)}
              onKeyDown={e => e.key === "Enter" && save()}
              data-testid="alarm-title-input" />

            {/* Tekrar seçici — pill row */}
            <div className="flex flex-wrap gap-1.5">
              {REPEAT_OPTS.map(o => (
                <button key={o.v} onClick={() => set("repeat", o.v)}
                  data-testid={`alarm-repeat-${o.v}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all
                    ${form.repeat === o.v ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-[#E5322A]/40"}`}>
                  {o.l}
                </button>
              ))}
            </div>

            {/* Gün seçici (haftalık seçilince göster) */}
            {needWeekdays && (
              <div className="flex gap-1.5 justify-between">
                {DAYS.map((d, i) => (
                  <button key={i} onClick={() => toggleDay(i)}
                    data-testid={`alarm-day-${i}`}
                    className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold border transition-all
                      ${form.weekdays.includes(i) ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-[#E5322A]/40"}`}>
                    {d}
                  </button>
                ))}
              </div>
            )}

            {/* Tarih (bir kez / aylık / yıllık) */}
            {needDate && (
              <input type="date" className="zen-input !text-sm w-full" value={form.date}
                onChange={e => set("date", e.target.value)} data-testid="alarm-date-input" />
            )}

            {/* Not (isteğe bağlı, collapse) */}
            <input className="zen-input !text-sm w-full text-slate-500" placeholder="Not... (isteğe bağlı)"
              value={form.note} onChange={e => set("note", e.target.value)} />

            {/* Kaydet / Vazgeç */}
            <div className="flex gap-2">
              <button onClick={() => { setAdding(false); setForm(emptyForm()); }}
                className="flex-1 zen-btn-ghost !text-xs">Vazgeç</button>
              <button onClick={save} className="flex-2 zen-btn-primary !text-sm flex-1" data-testid="alarm-save-btn">
                <Check className="w-4 h-4" /> Kaydet
              </button>
            </div>
          </div>
        )}

        {/* ── Alarm listesi ── */}
        <div className="p-3 space-y-2">
          {loading ? (
            <div className="text-center py-6 text-slate-400 text-sm">Yükleniyor...</div>
          ) : list.length === 0 && !adding ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
              Henüz alarm yok. "Yeni" ile ekle.
            </div>
          ) : (
            list.map(r => (
              <div key={r.id}
                className={`flex items-center gap-3 rounded-xl p-3 border transition-all
                  ${r.active ? "border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1623]"
                              : "border-slate-100 dark:border-[#1A2540] opacity-50 bg-slate-50 dark:bg-transparent"}`}>
                {/* On/Off toggle */}
                <button onClick={() => toggle(r)}
                  className={`w-11 h-6 rounded-full relative transition-all shrink-0 ${r.active ? "bg-[#E5322A]" : "bg-slate-300 dark:bg-[#22304A]"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${r.active ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-black tabular-nums">{r.time}</span>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">{r.title}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap gap-1.5">
                    <span className="zen-chip !text-[9px] !py-0">{repeatLabel(r)}</span>
                    {r.date && ["once","monthly","yearly"].includes(r.repeat) && <span>{r.date}</span>}
                    {r.note && <span className="truncate max-w-[120px]">{r.note}</span>}
                  </div>
                </div>
                <button onClick={() => del(r)} className="shrink-0 text-slate-300 hover:text-rose-500 transition-colors p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Bildirim uyarısı */}
        {Notification.permission !== "granted" && (
          <div className="mx-3 mb-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-700 dark:text-amber-400 text-center">
            Alarm sesi için bildirim iznine ihtiyaç var.{" "}
            <button className="underline font-bold" onClick={requestNotifPerm}>İzin Ver</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   ManualTerminModal — termin takvimi + butonu
═════════════════════════════════════════════ */
export function ManualTerminModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", date: todayStr(), note: "", repeat: "once", active: true, type: "termin" });
  const [saving, setSaving] = useState(false);
  const titleRef = useRef(null);
  useEffect(() => { setTimeout(() => titleRef.current?.focus(), 50); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Başlık ve tarih gerekli"); return; }
    setSaving(true);
    await api.post("/reminders", form);
    toast.success("Termin eklendi");
    setSaving(false);
    onSaved?.(); onClose();
  };

  return (
    <div className="fixed inset-0 z-[86] bg-black/60 flex items-center justify-center p-4"
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="zen-panel w-full max-w-sm zen-anim-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A]">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#E5322A]" />
            <span className="font-bold text-sm">Manuel Termin</span>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <input ref={titleRef} className="zen-input !text-sm w-full"
            placeholder="Açıklama (montaj, teslim, randevu...)"
            value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && save()}
            data-testid="termin-title-input" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-400 block mb-1">Tarih</label>
              <input type="date" className="zen-input !text-sm w-full" value={form.date}
                onChange={e => setForm(p => ({ ...p, date: e.target.value }))} data-testid="termin-date-input" />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 block mb-1">Tekrar</label>
              <select className="zen-input !text-sm w-full" value={form.repeat}
                onChange={e => setForm(p => ({ ...p, repeat: e.target.value }))}>
                {REPEAT_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>
          <input className="zen-input !text-sm w-full" placeholder="Not (isteğe bağlı)"
            value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
        </div>
        <div className="px-4 pb-4 flex gap-2">
          <button onClick={onClose} className="flex-1 zen-btn-ghost !text-xs">Vazgeç</button>
          <button onClick={save} disabled={saving} className="flex-1 zen-btn-primary !text-xs" data-testid="termin-save-btn">
            <Plus className="w-3.5 h-3.5" /> Ekle
          </button>
        </div>
      </div>
    </div>
  );
}

/* Manuel terminleri çeken hook */
export function useManualTermins() {
  const [termins, setTermins] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reminders");
      setTermins(data.filter(r => r.type === "termin" && r.active));
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  return { termins, reload: load };
}
